import { FC, useEffect, useState } from 'react';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import MovablePlaceholder from 'components/ui/MovablePlaceholder/MovablePlaceholder';
import Button from 'components/ui/Button/Button';
import { useClickAway } from 'hooks/useClickAway';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPeople } from 'constants/dynamic';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface People {
  numberOfAdults: number;
  numberOfChildren: number;
  numberOfSeniors: number;
  numberOfInfants: number;
  roomsString: string;
  childrenAges?: number[];
}

const getChildAges = () => {
  const ages = [];
  for (let i = 2; i <= 12; i++) {
    ages.push({ value: i, label: i.toString() });
  }
  return ages;
};

interface PeopleDropdownProps {
  onChange: (value: People) => void;
  movablePlaceholderIcon?: any;
  movablePlaceholder?: string;
  placeholder?: string;
}

const PeopleDropdown: FC<PeopleDropdownProps> = (props) => {
  const [open, setOpen] = useState(false);
  const [urlParams] = useSearchParams();
  const [people, setPeople] = useState({
    numberOfAdults: 2,
    numberOfChildren: 0,
    numberOfSeniors: 0,
    numberOfInfants: 0,
    childrenAges: [] as number[],
  });
  const [peopleString, setPeopleString] = useState(urlParams.get('peopleString'));
  const [t] = useTranslation();
  const defaultChildAge = 7;

  const ref = useClickAway(() => {
    setOpen(false);
  });

  const formatPersons = (newPeople: People) => {
    let resultArr: string[] = [];
    if (newPeople.numberOfAdults) {
      const adultStr = newPeople.numberOfAdults > 1 ? t('search.adults') : t('search.adult');
      resultArr.push(`${newPeople.numberOfAdults} ${adultStr}`);
    }

    if (newPeople.numberOfChildren) {
      const childrenStr = newPeople.numberOfChildren > 1 ? t('search.children') : t('search.child');
      resultArr.push(`${newPeople.numberOfChildren} ${childrenStr}`);
    }
    if (newPeople.numberOfSeniors) {
      const seniorsStr = newPeople.numberOfSeniors > 1 ? t('search.seniors') : t('search.senior');
      resultArr.push(`${newPeople.numberOfSeniors} ${seniorsStr}`);
    }

    if (newPeople.numberOfInfants) {
      const infantsStr = newPeople.numberOfInfants > 1 ? t('search.infants') : t('search.infant');
      resultArr.push(`${newPeople.numberOfInfants} ${infantsStr}`);
    }

    return resultArr.join(', ');
  };

  useEffect(() => {
    onChange(2, 0, 0, 0, []);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (
    adults: number,
    children: number,
    seniors: number,
    infants: number,
    childrenAges?: number[]
  ) => {
    const adultsCount = adults + seniors;
    const childrenCount = children;
    const infantsCount = infants;

    let updatedChildrenAges = childrenAges || people.childrenAges || [];
    if (updatedChildrenAges.length < children) {
      while (updatedChildrenAges.length < children) {
        updatedChildrenAges.push(defaultChildAge);
      }
    } else if (updatedChildrenAges.length > children) {
      updatedChildrenAges = updatedChildrenAges.slice(0, children);
    }

    let roomsString = adultsCount + '';
    for (let i = 0; i < childrenCount; i++) {
      roomsString += `_${updatedChildrenAges[i] || defaultChildAge}`;
    }
    for (let i = 0; i < infantsCount; i++) {
      roomsString += `_1`;
    }

    const newPeople = {
      numberOfAdults: adults,
      numberOfChildren: children,
      numberOfSeniors: seniors,
      numberOfInfants: infants,
      roomsString,
      childrenAges: updatedChildrenAges,
    };

    const newPeopleString = formatPersons(newPeople);
    setPeople(newPeople);
    setPeopleString(newPeopleString);

    props.onChange(newPeople);
  };

  const onChangeChildAge = (childIndex: number, age: number) => {
    const updatedAges = [...(people.childrenAges || [])];
    while (updatedAges.length < people.numberOfChildren) {
      updatedAges.push(defaultChildAge);
    }
    updatedAges[childIndex] = age;
    onChange(
      people.numberOfAdults,
      people.numberOfChildren,
      people.numberOfSeniors,
      people.numberOfInfants,
      updatedAges
    );
  };

  return (
    <div className="position-relative">
      <div
        className={
          'room-dropdown-wr form-control' +
          (props.movablePlaceholder ? ' movable-placeholder-input' : '')
        }
        onClick={() => setOpen(true)}
      >
        {peopleString}
      </div>
      {props.movablePlaceholder && (
        <MovablePlaceholder
          label={props.movablePlaceholder}
          showSmall={!!peopleString}
          icon={props.movablePlaceholderIcon}
        />
      )}
      {open && (
        <div ref={ref} className="persons">
          <div className="persons-row">
            <span>{t('search.adults')} (12-64)</span>
            <SelectCustom
              options={getPeople()}
              value={people.numberOfAdults}
              placeholder={props.placeholder}
              onChange={(value) =>
                onChange(
                  value,
                  people.numberOfChildren,
                  people.numberOfSeniors,
                  people.numberOfInfants,
                  people.childrenAges
                )
              }
            />
          </div>
          <div className="persons-row">
            <span>{t('search.children')} (2-12)</span>
            <SelectCustom
              options={getPeople()}
              value={people.numberOfChildren}
              onChange={(value) =>
                onChange(
                  people.numberOfAdults,
                  value,
                  people.numberOfSeniors,
                  people.numberOfInfants,
                  people.childrenAges
                )
              }
            />
          </div>
          {people.numberOfChildren > 0 && (
            <div className="persons-row">
              <span>{t('search.age').replace('16', '12')}</span>
              <div>
                {Array.from({ length: people.numberOfChildren }).map((_, index) => (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    <SelectCustom
                      options={getChildAges()}
                      value={people.childrenAges[index] || 2}
                      onChange={(value) => onChangeChildAge(index, value)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="persons-row">
            <span>{t('search.seniors')} (65+)</span>
            <SelectCustom
              options={getPeople()}
              value={people.numberOfSeniors}
              onChange={(value) =>
                onChange(
                  people.numberOfAdults,
                  people.numberOfChildren,
                  value,
                  people.numberOfInfants,
                  people.childrenAges
                )
              }
            />
          </div>
          <div className="persons-row">
            <span>{t('search.infants')} (0-2)</span>
            <SelectCustom
              options={getPeople()}
              value={people.numberOfInfants}
              onChange={(value) =>
                onChange(
                  people.numberOfAdults,
                  people.numberOfChildren,
                  people.numberOfSeniors,
                  value,
                  people.childrenAges
                )
              }
            />
          </div>
          <div className="persons-row">
            <span></span>
            <Button
              className="btn btn-sm btn-primary"
              title={t('close')}
              onClick={() => setOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(PeopleDropdown, 'PeopleDropdown');
